import Big from 'big.js';
import { getCreditCardType } from 'cleave-zen';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import { Start, Stop } from 'smg/buttons';
import { GetCountry, GetState } from 'smg/countries';
import * as hyejoo from 'smg/customer-api/hyejoo';
import { Redirect, Session } from 'smg/customer-api/session';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { GetProofURLs } from 'smg/proofs';
import { G } from 'smg/querystring';
import { Clone } from 'smg/template';
import { DollarFormat } from 'smg/util';
import { FormatAddressOneLine } from '../../address';
import { AddCartItem } from '../../carts';
import { Relref } from '../../hugo';
import { GetOrderStatus } from '../../order';
import { MakePayment } from '../../payments';
import { SwiperInit } from '../../swiper';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

declare interface orderResp {
    order: models.Order;
}

if (document.body.id === 'account-order') {
    void main();
}

export async function main() {
    if (await Redirect(undefined, Relref('/'))) {
        return;
    }

    // Start batch requests
    const b = new Batch();

    const carriers = b.get<hyejoo.Carriers>('/carriers', {
        params: {
            'data': 'name,link',
        },
    }).then(resp => {
        const m = new Map<number, models.Carrier>();
        for (const carrier of resp.data.carriers) {
            m.set(carrier.id ?? 0, carrier);
        }
        return m;
    });

    const lineItems = b.get<hyejoo.LineItems>('/v2/line-items', {
        params: {
            'data': 'name,pluralName,product',
        },
    }).then(resp => {
        const m = new Map<string, models.LineItem>();
        for (const lineItem of resp.data.lineItems) {
            m.set(lineItem.id ?? '', lineItem);
        }
        return m;
    });

    void b.Exec();

    // Check for ID/Key/Invoice in query string
    const id = G().Get('id');
    if (!id) {
        showNotFound();
        return;
    }

    // Get Order
    const orderResp = await axios.get<orderResp>(`/orders/${id}`).then(resp => resp.data).catch(() => showNotFound());
    const order = orderResp?.order;

    if (order) {
        const $invoiceBtn = document.querySelector('.invoice-btn') as HTMLAnchorElement;
        $invoiceBtn.href = `${window.apiRoot}/orders/${order.id || ''}/invoice`;

        // Call Draw Funcs
        drawOrderTracking(order, await carriers);
        drawOrder(order, await lineItems);
        drawOrderPriceSummary(order);
        await drawShippingPayment(order);
        hideLoading();
        SwiperInit();
    }
}

// Helpers
function hideLoading() {
    const $spinner = document.querySelector('#order-spinner') as HTMLElement;
    $spinner.classList.add('d-none');
}

function showNotFound() {
    const $orderContainer = document.querySelector('#order-container') as HTMLElement;
    const $notFoundContainer = document.querySelector('#not-found-container') as HTMLElement;
    $orderContainer.classList.add('d-none');
    $notFoundContainer.classList.remove('d-none');
    hideLoading();
}

function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function progressBarFill($containerEl: HTMLElement, className: string) {
    const $children = Array.from($containerEl.children) as HTMLElement[];
    for (const child of $children) {
        const $nextSeparator = child.previousElementSibling;
        for (const innerChild of Array.from(child.children)) {
            innerChild.classList.add('complete');
        }
        if ($nextSeparator) {
            $nextSeparator.classList.add('complete');
        }
        if (child.classList.contains(className)) {
            break;
        }
    }
}

// Draw Funcs
function drawOrderTracking(order: models.Order, carriers: Map<number, models.Carrier>) {
    const $orderContainer = document.querySelector('#order-container') as HTMLElement;
    const $orderCard = Clone('#order-card-tracking-template').firstElementChild as HTMLElement;
    const $invoice = $orderCard.querySelector('.invoice') as HTMLElement;
    const $trackingHeader = $orderCard.querySelector('.tracking-header') as HTMLElement;
    const $orderDate = $orderCard.querySelector('.order-date') as HTMLElement;
    const $taxExemption = $orderCard.querySelector('.tax-exemption') as HTMLElement;
    const $statusText = $orderCard.querySelector('.status-text') as HTMLElement;
    const $statusTextInfo = $orderCard.querySelector('.status-text-info') as HTMLElement;
    const $viewTrackingInfoBtn = $orderCard.querySelector('.view-tracking-info-btn') as HTMLButtonElement;

    const orderStatus = GetOrderStatus(order);

    $invoice.textContent = `Order#: ${order.invoiceNumber ?? ''}`;

    const $orderProgressBar = $orderCard.querySelector('#order-progress-container') as HTMLElement;

    switch (orderStatus.status) {
    case 'Awaiting Payment':
        $trackingHeader.innerHTML = '<span class="material-symbols-rounded me-1">pan_tool</span> Your order is awaiting payment!';
        $statusTextInfo.textContent = 'Production of your order will commence upon receipt of full payment.';
        progressBarFill($orderProgressBar, 'order-progress-stage-awaiting-payment');
        break;
    case 'In Production':
        $trackingHeader.innerHTML = '<span class="material-symbols-rounded me-1">conveyor_belt</span> Your order is in production!';
        $statusTextInfo.textContent = 'Your order is in production and will be shipped once completed.';
        progressBarFill($orderProgressBar, 'order-progress-stage-in-production');
        break;
    case 'On the Way':
        $trackingHeader.innerHTML = '<span class="material-symbols-rounded me-1">local_shipping</span> Your order is on the way!';
        $statusTextInfo.textContent = 'Your order has shipped and is on its way to you!';
        progressBarFill($orderProgressBar, 'order-progress-stage-on-the-way');
        break;
    case 'Delivered':
        $trackingHeader.innerHTML = '<span class="material-symbols-rounded me-1">task_alt</span> Your order has been delivered!';
        $statusTextInfo.textContent = 'Your order has been delivered. We hope you enjoy your purchase!';
        progressBarFill($orderProgressBar, 'order-progress-stage-delivered');
        break;
    default:
        $trackingHeader.innerHTML = '<span class="material-symbols-rounded me-1">help</span> Order Status Unknown';
        $statusTextInfo.textContent = 'The status of your order is currently unknown. Please contact support for assistance.';
        break;
    }

    if (order.created) {
        $orderDate.textContent = $orderDate.textContent = new Date(order.created).toLocaleDateString();
    }

    $taxExemption.textContent = order.userTaxExemptionOrders?.length ? 'Yes' : 'No';
    $statusText.textContent = orderStatus.status;

    // Pay Balance Button
    if (order.balance && Big(order.balance).gt(0.05)) {
        const $payBalanceBtn = Clone('#pay-balance-link-btn-template').firstElementChild as HTMLElement;
        const $btn = $payBalanceBtn?.querySelector('button') as HTMLButtonElement;
        $btn.textContent = `Pay Balance: ${DollarFormat(order.balance ?? 0)}`;
        $btn.addEventListener('click', async () => {
            await MakePayment(await Session(), order);
        });

        const $statusTextContainer = $orderCard.querySelector('.status-text-container') as HTMLElement;
        $statusTextContainer.appendChild($payBalanceBtn);
    }

    let hasShipments = false;
    if (order.orderShippingOptions?.length) {
        for (const shippingOption of order.orderShippingOptions) {
            if (shippingOption.orderShippingOptionShipments?.length) {
                hasShipments = true;
                break;
            }
        }
        if (hasShipments) {
            $viewTrackingInfoBtn.classList.remove('d-none');
            $viewTrackingInfoBtn.addEventListener('click', () => {
                const $body = Clone('#shipments-modal-body-template').firstElementChild as HTMLElement;
                for (const orderShippingOption of order.orderShippingOptions ?? []) {
                    if (orderShippingOption.orderShippingOptionShipments?.length) {
                        for (const orderShipments of orderShippingOption.orderShippingOptionShipments ?? []) {
                            const shipment = orderShipments.shipment;
                            if (!shipment) {
                                continue;
                            }

                            const carrier = carriers.get(shipment.carrierID ?? 0);

                            const $el = Clone('#shipment-tracking-card-template').firstElementChild as HTMLElement;
                            const $carrier = $el.querySelector('.carrier') as HTMLElement;
                            const $trackingID = $el.querySelector('.tracking-id') as HTMLElement;
                            const $carrierLink = $el.querySelector('.tracking-link-btn') as HTMLAnchorElement;

                            $carrier.textContent = carrier?.name || 'Unknown Carrier';
                            $trackingID.textContent = shipment.trackingNumber || 'No Tracking ID';
                            $carrierLink.href = (carrier?.link ?? '').replace('%%', shipment.trackingNumber ?? '');

                            $body.appendChild($el);
                        }
                    }
                }
                new Modal({
                    title: 'Tracking IDs',
                    headerLevel: 'h2',
                    body: $body,
                    appendBodyClass: 'px-4 px-sm-20',
                    cancelButtonClass: 'btn btn-primary',
                    cancelText: 'Close',
                });
            });
        }
    }

    $orderContainer.appendChild($orderCard);
}

function drawOrder(order: models.Order, lineItems: Map<string, models.LineItem>) {
    const $orderContainer = document.querySelector('#order-container') as HTMLElement;
    const $card = Clone('#order-item-card-template').firstElementChild as HTMLElement;
    const $orderItemsContainer = $card.querySelector('.order-items-container') as HTMLElement;
    const $reorderBtn = $card.querySelector('.reorder-link') as HTMLButtonElement;

    // Order Details
    const showableOrderLineItems: models.OrderLineItem[] = [];
    for (const orderLineItem of order.orderLineItems ?? []) {
        if (!orderLineItem.quantity) {
            continue;
        }

        const lineItem = lineItems.get(orderLineItem.lineItemID || '');
        if (!lineItem?.product) {
            continue;
        }
        if (!orderLineItem.lineItemModel) {
            continue;
        }

        showableOrderLineItems.push(orderLineItem);
    }
    if (showableOrderLineItems.length) {
        const orderLineItemProofIDs: string[] = [];
        for (let i = 0; i < showableOrderLineItems.length; i++) {
            const orderLineItem = showableOrderLineItems[i];

            const $el = Clone('#order-item-details-template').firstElementChild as HTMLElement;
            const $proofsContainer = $el.querySelector('.proofs-container') as HTMLElement;
            const $orderDetailsContainer = $el.querySelector('.order-details-container') as HTMLElement;
            const $lineItemType = $orderDetailsContainer.querySelector('.line-item-type') as HTMLElement;
            const $qty = $orderDetailsContainer.querySelector('.qty') as HTMLElement;
            const $size = $orderDetailsContainer.querySelector('.size') as HTMLElement;

            const lineItem = lineItems.get(orderLineItem.lineItemID || '');

            const name = orderLineItem.lineItem || (lineItem as models.LineItem).pluralName || (lineItem as models.LineItem).name || 'Other';
            const type = orderLineItem.lineItemType?.pluralName || orderLineItem.lineItemType?.name || '';
            const size = orderLineItem.lineItemTypeSize?.name || orderLineItem.size || '';

            $lineItemType.textContent = `${type} ${name}`;
            $qty.textContent = `Quantity: ${orderLineItem.quantity || ''}`;
            $size.textContent = `Size: ${size}`;

            // Proof Images
            if (orderLineItem.orderProof) {
                const urls = GetProofURLs(window.cloudFront, orderLineItem.orderProof.proof?.file || '', orderLineItem.orderProof.proof?.path || '');

                const el = Clone('#order-proof-template');

                const a = el.querySelector('a') as HTMLAnchorElement;
                a.href = urls.main;
                a.setAttribute('data-fancybox', order.id ?? '');

                const img = a.querySelector('img') as HTMLImageElement;
                img.src = urls.thumb;
                img.alt = orderLineItem.orderProof.proof?.name || '';
                img.style.maxWidth = '7.5rem';
                img.style.maxHeight = '7.5rem';

                $proofsContainer?.appendChild(a);

                orderLineItemProofIDs.push(orderLineItem.orderProof.id || '');
            } else {
                $proofsContainer?.remove();
            }

            $orderItemsContainer.appendChild($el);
            if (showableOrderLineItems.length > 1 && i < showableOrderLineItems.length - 1) {
                const $separator = Clone('#order-item-details-separator-template').firstElementChild as HTMLElement;
                $orderItemsContainer.appendChild($separator);
            }
        }

        if (order.orderProofs?.length) {
            let hasUniqueProofs = false;
            const $container = Clone('#order-proofs-template').firstElementChild as HTMLElement;
            const $swiperContainer = $container.querySelector('.swiper-wrapper') as HTMLElement;
            for (const orderProof of order.orderProofs) {
                if (orderLineItemProofIDs.includes(orderProof.id || '')) {
                    continue;
                }

                hasUniqueProofs = true;

                const urls = GetProofURLs(window.cloudFront, orderProof.proof?.file || '', orderProof.proof?.path || '');

                const el = Clone('#order-proof-swiper-template');

                const a = el.querySelector('a') as HTMLAnchorElement;
                a.href = urls.main;
                a.setAttribute('data-fancybox', order.id ?? '');

                const img = a.querySelector('img') as HTMLImageElement;
                img.src = urls.thumb;
                img.alt = orderProof.proof?.name || '';
                img.style.maxWidth = '7.5rem';
                img.style.maxHeight = '7.5rem';

                $swiperContainer?.appendChild(a);
            }

            if (hasUniqueProofs) {
                $orderItemsContainer.appendChild($container);
            }
        }
    } else {
        return;
    }

    $reorderBtn?.addEventListener('click', async () => {
        Start($reorderBtn);
        await AddCartItem({ orderID: order.id ?? '' }).finally(() => Stop($reorderBtn));
    });

    $orderContainer.parentElement?.appendChild($card);
}

async function drawShippingPayment(order: models.Order) {
    if (!order.orderShippingOptions || order.orderShippingOptions.length === 0) {
        return;
    }
    const $orderContainer = document.querySelector('#order-container') as HTMLElement;
    const $shippingEls = createOrderShippingEls(order.orderShippingOptions);
    const $paymentEls = await createOrderPaymentEls(order.orderPayments ?? []);

    if ($shippingEls.length < 2 && $paymentEls.length < 2) {
        const $shippingPaymentEl = Clone('#shipping-payment-card-template').firstElementChild as HTMLElement;
        const $orderSummaryEl = drawOrderPriceSummary(order, true) as HTMLElement;
        $orderSummaryEl.className = 'd-none d-sm-flex flex-column gap-3 flex-grow-1';
        const $container = document.createElement('div');
        $container.classList.add('d-flex', 'flex-column', 'gap-4', 'flex-xxl-row');
        if ($shippingEls.length) {
            $container.appendChild($shippingEls[0].querySelector('.card')?.firstElementChild as HTMLElement);
        }
        if ($paymentEls.length) {
            $paymentEls[0].firstElementChild?.classList.add('ms-xxl-8');
            $container.appendChild($paymentEls[0].querySelector('.card')?.firstElementChild as HTMLElement);
        }
        $shippingPaymentEl.appendChild($container);
        $shippingPaymentEl.appendChild($orderSummaryEl as HTMLElement);
        $orderContainer.appendChild($shippingPaymentEl);
    } else {
        const $shippingOptionsContainer = Clone('#multiple-shipping-options-container').firstElementChild as HTMLElement;
        const $paymentOptionsContainer = Clone('#multiple-payment-options-container').firstElementChild as HTMLElement;
        for (const $shippingEl of $shippingEls) {
            $shippingOptionsContainer.appendChild($shippingEl);
        }
        for (const $paymentEl of $paymentEls) {
            $paymentOptionsContainer.appendChild($paymentEl);
        }
        $orderContainer.appendChild($shippingOptionsContainer);
        $orderContainer.appendChild($paymentOptionsContainer);
    }
}

function createOrderShippingEls(shippingOptions: models.OrderShippingOption[]): HTMLElement[] {
    const $shippingEls: HTMLElement[] = [];
    for (const shippingOption of shippingOptions) {
        const $shippingCard = Clone('#shipping-card-template').firstElementChild as HTMLElement;
        const $name = $shippingCard.querySelector('.shipping-name') as HTMLElement;
        const $address = $shippingCard.querySelector('.shipping-address-line') as HTMLElement;
        const $cityStateZip = $shippingCard.querySelector('.shipping-city-state-zip-country') as HTMLElement;
        const $phone = $shippingCard.querySelector('.shipping-phone') as HTMLElement;
        const $email = $shippingCard.querySelector('.shipping-email') as HTMLElement;

        $name.textContent = shippingOption.shippingOption?.fullName || '';
        $address.textContent = shippingOption.shippingOption?.addressLine1 || '';
        $cityStateZip.textContent = `${shippingOption.shippingOption?.city || ''}, ${shippingOption.shippingOption?.state?.stateCode || ''} ${shippingOption.shippingOption?.zip || ''} ${shippingOption.shippingOption?.country?.countryCode2 || ''}`.trim();
        $phone.textContent = shippingOption.shippingOption?.phone || '';
        $email.textContent = shippingOption.shippingOption?.email || '';

        $shippingEls.push($shippingCard);
    }
    return $shippingEls;
}

async function createOrderPaymentEls(payments: models.OrderPayment[]): Promise<HTMLElement[]> {
    const $paymentEls: HTMLElement[] = [];
    for (const payment of payments) {
        const $paymentCard = Clone('#payment-card-template').firstElementChild as HTMLElement;
        const $paymentMethodContainer = $paymentCard.querySelector('.payment-method-container') as HTMLElement;

        const paymentOption = payment.paymentOption;
        switch (paymentOption?.paymentOptionTypeID) {
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT: {
            const $paymentOptionEl = Clone('#payment-option-template-credit').firstElementChild as HTMLElement;

            const $ccLastFour = $paymentOptionEl.querySelector('.cc-last-four') as HTMLElement;
            const $ccIssuer = $paymentOptionEl.querySelector('.cc-issuer') as HTMLElement;
            const paymentOptionAddress: models.ShippingOption = {
                city: paymentOption.city,
                state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
                zip: paymentOption.zip,
                country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
            };
            const $address = $paymentOptionEl.querySelector('.cc-address') as HTMLElement;
            $address.innerHTML = await FormatAddressOneLine(paymentOptionAddress);
            if (paymentOption.creditCardLast4) {
                $ccLastFour.textContent = paymentOption.creditCardLast4.toString().padStart(4, '0');
            }
            if (paymentOption.creditCardFirst6) {
                switch (getCreditCardType(paymentOption.creditCardFirst6.toString()).toString()) {
                case 'amex':
                    $ccIssuer.textContent = 'Amex';
                    $paymentOptionEl.querySelector('.cc-icon-generic')?.classList.add('d-none');
                    $paymentOptionEl.querySelector('.cc-icon-amex')?.classList.remove('d-none');
                    break;
                case 'discover':
                    $ccIssuer.textContent = 'Discover';
                    $paymentOptionEl.querySelector('.cc-icon-generic')?.classList.add('d-none');
                    $paymentOptionEl.querySelector('.cc-icon-discover')?.classList.remove('d-none');
                    break;
                case 'mastercard':
                    $ccIssuer.textContent = 'Mastercard';
                    $paymentOptionEl.querySelector('.cc-icon-generic')?.classList.add('d-none');
                    $paymentOptionEl.querySelector('.cc-icon-mastercard')?.classList.remove('d-none');
                    break;
                case 'visa':
                    $ccIssuer.textContent = 'Visa';
                    $paymentOptionEl.querySelector('.cc-icon-generic')?.classList.add('d-none');
                    $paymentOptionEl.querySelector('.cc-icon-visa')?.classList.remove('d-none');
                    break;
                }
            } else {
                if ($ccIssuer.parentElement) {
                    $ccIssuer.parentElement.textContent = 'Credit';
                }
            }
            $paymentMethodContainer.appendChild($paymentOptionEl);
            $paymentEls.push($paymentCard);
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK: {
            const $paymentOptionEl = Clone('#payment-option-template-bank').firstElementChild as HTMLElement;

            const bankType = $paymentOptionEl.querySelector('.bank-acct-type') as HTMLElement;
            if (paymentOption.bankType && paymentOption.accountType) {
                bankType.textContent = `${capitalizeFirstLetter(paymentOption.bankType)} ${capitalizeFirstLetter(paymentOption.accountType)} Account`;
                if (paymentOption.creditCardLast4) {
                    bankType.textContent += ` (...${paymentOption.creditCardLast4})`;
                }
            }
            const poAddress: models.ShippingOption = {
                city: paymentOption.city,
                state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
                zip: paymentOption.zip,
                country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
            };
            const $address = $paymentOptionEl.querySelector('.payment-option-address') as HTMLElement;
            $address.innerHTML = await FormatAddressOneLine(poAddress);

            $paymentMethodContainer.appendChild($paymentOptionEl);
            $paymentEls.push($paymentCard);
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK: {
            const $paymentOptionEl = Clone('#payment-option-template-check').firstElementChild as HTMLElement;

            const poAddress: models.ShippingOption = {
                city: paymentOption.city,
                state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
                zip: paymentOption.zip,
                country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
            };
            const $address = $paymentOptionEl.querySelector('.payment-option-address') as HTMLElement;
            $address.innerHTML = await FormatAddressOneLine(poAddress);

            $paymentMethodContainer.appendChild($paymentOptionEl);
            $paymentEls.push($paymentCard);
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE: {
            const $paymentOptionEl = Clone('#payment-option-template-balance').firstElementChild as HTMLElement;

            const poAddress: models.ShippingOption = {
                city: paymentOption.city,
                state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
                zip: paymentOption.zip,
                country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
            };
            const $address = $paymentOptionEl.querySelector('.payment-option-address') as HTMLElement;
            $address.innerHTML = await FormatAddressOneLine(poAddress);

            $paymentMethodContainer.appendChild($paymentOptionEl);
            $paymentEls.push($paymentCard);
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL: {
            const $paymentOptionEl = Clone('#payment-option-template-paypal').firstElementChild as HTMLElement;

            const poAddress: models.ShippingOption = {
                city: paymentOption.city,
                state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
                zip: paymentOption.zip,
                country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
            };
            const $address = $paymentOptionEl.querySelector('.payment-option-address') as HTMLElement;
            $address.innerHTML = await FormatAddressOneLine(poAddress);

            $paymentMethodContainer.appendChild($paymentOptionEl);
            $paymentEls.push($paymentCard);
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO: {
            const $paymentOptionEl = Clone('#payment-option-template-purchase-order').firstElementChild as HTMLElement;

            const poAddress: models.ShippingOption = {
                city: paymentOption.city,
                state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
                zip: paymentOption.zip,
                country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
            };
            const $address = $paymentOptionEl.querySelector('.payment-option-address') as HTMLElement;
            $address.innerHTML = await FormatAddressOneLine(poAddress);

            $paymentMethodContainer.appendChild($paymentOptionEl);
            $paymentEls.push($paymentCard);
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE: {
            const $paymentOptionEl = Clone('#payment-option-template-wire-transfer').firstElementChild as HTMLElement;

            const poAddress: models.ShippingOption = {
                city: paymentOption.city,
                state: await GetState(paymentOption.country?.id ?? paymentOption.countryID, paymentOption.state?.id ?? paymentOption.stateID),
                zip: paymentOption.zip,
                country: await GetCountry(paymentOption.country?.id ?? paymentOption.countryID),
            };
            const $address = $paymentOptionEl.querySelector('.payment-option-address') as HTMLElement;
            $address.innerHTML = await FormatAddressOneLine(poAddress);

            $paymentMethodContainer.appendChild($paymentOptionEl);
            $paymentEls.push($paymentCard);
            break;
        }
        }
    }
    return $paymentEls;
}

function drawOrderPriceSummary(order: models.Order, returnEl: boolean = false) {
    const $orderContainer = document.querySelector('#order-container') as HTMLElement;
    const $orderSummaryContainer = Clone('#order-summary-template').firstElementChild as HTMLElement;

    const $savingsRow = $orderSummaryContainer.querySelector('.price-row.savings') as HTMLElement;
    const $shippingRow = $orderSummaryContainer.querySelector('.price-row.shipping') as HTMLElement;
    const $taxRow = $orderSummaryContainer.querySelector('.price-row.tax') as HTMLElement;
    const $subtotalRow = $orderSummaryContainer.querySelector('.price-row.subtotal') as HTMLElement;
    const $totalRow = $orderSummaryContainer.querySelector('.price-row.total') as HTMLElement;
    const $pendingBalanceRow = $orderSummaryContainer.querySelector('.price-row.pending-balance') as HTMLElement;

    const nums = {
        subtotal: Big('0'),
        discounts: Big('0'),
        shipping: Big('0'),
        tax: Big('0'),
        total: Big('0'),
    };

    let shippingUSA = true;
    const promoCodes: string[] = [];

    for (const orderLineItem of order.orderLineItems ?? []) {
        if (orderLineItem.lineItemID === models.LineItemID.LINE_ITEMS_PROMO_CODE || orderLineItem.lineItemModel?.id === models.LineItemID.LINE_ITEMS_PROMO_CODE) {
            if (orderLineItem.type && !promoCodes.includes(orderLineItem.type)) {
                promoCodes.push(orderLineItem.type);
            }
        }

        if (Big(orderLineItem.price || 0).lt(0)) {
            nums.discounts = nums.discounts.add(orderLineItem.price || 0);
        }

        const subtotal = Big(orderLineItem.price || 0).mul(orderLineItem.quantity || 0);
        nums.total = nums.total.add(subtotal);

        const childLineItems = (orderLineItem as models.OrderLineItem).orderChildLineItems || [];
        for (const childLineItem of childLineItems) {
            const subtotal = Big(childLineItem.price || 0).mul(childLineItem.quantity || 0);
            nums.total = nums.total.add(subtotal);

            if (Big(childLineItem.price || 0).lt(0)) {
                nums.discounts = nums.discounts.add(childLineItem.price || 0);
            }
        }

        if (orderLineItem.lineItemModel?.shipping) {
            nums.shipping = nums.shipping.add(Big(orderLineItem.price || 0) || 0);
        }

        if (orderLineItem.lineItemModel?.tax) {
            nums.tax = nums.tax.add(Big(orderLineItem.price || 0) || 0);
        }
    }

    for (const orderShippingOption of order.orderShippingOptions ?? []) {
        if (orderShippingOption.shippingOption?.country?.countryCode2 !== 'US') {
            shippingUSA = false;
            break;
        }
    }

    // Savings
    if (nums.discounts.gt(0)) {
        const $savingsPrice = $savingsRow.querySelector('.price-row-value') as HTMLElement;
        $savingsPrice.textContent = nums.discounts.toFixed(2);

        if (promoCodes.length > 0) {
            const $promoCodeContainer = $savingsRow.querySelector('.promo-code-container') as HTMLElement;
            for (const promoCode of promoCodes) {
                const $el = Clone('#promo-code-template').firstElementChild as HTMLElement;
                const $promoCode = $el.querySelector('.promo-code-text') as HTMLElement;
                $promoCode.textContent = promoCode;
                $promoCodeContainer.appendChild($el);
            }
        }
    } else {
        $savingsRow.classList.add('d-none');
    }

    // Shipping
    const $shippingPrice = $shippingRow.querySelector('.price-row-value') as HTMLElement;
    if (nums.shipping.eq(0) && shippingUSA) {
        $shippingPrice.textContent = 'Free for the USA';
        $shippingPrice.classList.add('fst-italic');
    } else if (nums.shipping.eq(0)) {
        $shippingPrice.textContent = 'Free';
        $shippingPrice.classList.add('fst-italic');
    } else {
        $shippingPrice.textContent = DollarFormat(nums.shipping);
    }

    // Tax
    const $taxPrice = $taxRow.querySelector('.price-row-value') as HTMLElement;
    $taxPrice.textContent = DollarFormat(nums.tax);

    // Subtotal
    const $subtotalPrice = $subtotalRow.querySelector('.price-row-value') as HTMLElement;
    $subtotalPrice.textContent = DollarFormat(nums.total.sub(nums.tax).sub(nums.shipping).sub(nums.discounts));

    // Total
    const $totalPrice = $totalRow.querySelector('.price-row-value') as HTMLElement;
    $totalPrice.textContent = DollarFormat(order.total ?? nums.total);

    // Pending Balance
    if (order.balance && Big(order.balance).gte(0.05)) {
        const $pendingBalancePrice = $pendingBalanceRow.querySelector('.price-row-value') as HTMLElement;
        $pendingBalancePrice.textContent = DollarFormat(order.balance);
    } else {
        $pendingBalanceRow.classList.add('d-none');
    }

    if (returnEl) {
        return $orderSummaryContainer;
    }

    $orderSummaryContainer.classList.add('d-sm-none');
    if ($orderContainer.parentElement) {
        $orderContainer.parentElement.insertBefore($orderSummaryContainer, $orderContainer.parentElement.lastElementChild);
    }
}